import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import {
  Form,
  Button,
  Card,
  Container,
  Row,
  Col,
  Alert,
  InputGroup,
} from "react-bootstrap";
import { authAPI } from "../../api/auth";
import {
  FaLock,
  FaEye,
  FaEyeSlash,
  FaCheck,
  FaArrowLeft,
} from "react-icons/fa";
import Swal from "sweetalert2";
import * as Yup from "yup";
import { Formik } from "formik";

const ResetPassword = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [email_or_mobile, setEmailOrMobile] = useState("");

  useEffect(() => {
    if (location.state?.email_or_mobile) {
      setEmailOrMobile(location.state.email_or_mobile);
    }
  }, [location]);

  const validationSchema = Yup.object({
    otp: Yup.string()
      .matches(/^[0-9]{6}$/, "OTP must be 6 digits")
      .required("OTP is required"),
    new_password: Yup.string()
      .min(6, "Password must be at least 6 characters")

      .required("New password is required"),
    confirm_password: Yup.string()
      .oneOf([Yup.ref("new_password"), null], "Passwords must match")
      .required("Please confirm your password"),
  });

  const handleSubmit = async (values) => {
    setError("");
    setLoading(true);

    try {
      await authAPI.resetPassword({
        email_or_mobile: email_or_mobile || values.email_or_mobile,
        otp: values.otp,
        new_password: values.new_password,
        confirm_password: values.confirm_password,
      });

      Swal.fire({
        icon: "success",
        title: "Password Reset Successful!",
        text: "Your password has been reset successfully. Redirecting to login...",
        timer: 2000,
        showConfirmButton: false,
      });

      setSuccess(true);
      setTimeout(() => {
        navigate("/login");
      }, 2000);
    } catch (error) {
      console.error("Reset password error:", error);
      setError(error.response?.data?.message || "Failed to reset password");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container fluid className="auth-container">
      <Row className="justify-content-center align-items-center min-vh-100">
        <Col md={6} lg={4}>
          <div className="text-center mb-4">
            <Link
              to="/forgot-password"
              className="text-decoration-none d-inline-flex align-items-center mb-3"
            >
              <FaArrowLeft className="me-2" /> Back
            </Link>
            <h1 className="display-6 fw-bold text-primary">
              <span className="text-warning">V</span>Shape Gym
            </h1>
            <p className="text-muted">Reset your password</p>
          </div>

          <Card className="shadow-lg border-0">
            <Card.Body className="p-4">
              {error && (
                <Alert
                  variant="danger"
                  onClose={() => setError("")}
                  dismissible
                >
                  {error}
                </Alert>
              )}

              {success ? (
                <Alert variant="success">
                  <div className="d-flex align-items-center">
                    <FaCheck className="me-2" />
                    <div>
                      <h5 className="alert-heading mb-1">
                        Password Reset Successful!
                      </h5>
                      <p className="mb-0">Redirecting to login page...</p>
                    </div>
                  </div>
                </Alert>
              ) : (
                <>
                  <div className="text-center mb-4">
                    <div className="reset-password-icon mx-auto mb-3">
                      <FaLock size={48} className="text-success" />
                    </div>
                    <h5>Create New Password</h5>
                    <p className="text-muted">
                      Enter the OTP sent to your email/mobile and set a new
                      password
                    </p>
                  </div>

                  <Formik
                    initialValues={{
                      email_or_mobile: email_or_mobile,
                      otp: "",
                      new_password: "",
                      confirm_password: "",
                    }}
                    validationSchema={validationSchema}
                    onSubmit={handleSubmit}
                  >
                    {({
                      values,
                      errors,
                      touched,
                      handleChange,
                      handleBlur,
                      handleSubmit,
                      isValid,
                    }) => (
                      <Form onSubmit={handleSubmit}>
                        {!email_or_mobile && (
                          <Form.Group className="mb-3">
                            <Form.Label>Email or Mobile Number</Form.Label>
                            <Form.Control
                              type="text"
                              name="email_or_mobile"
                              value={values.email_or_mobile}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              placeholder="user@gmail.com or 9876543210"
                              isInvalid={
                                touched.email_or_mobile &&
                                errors.email_or_mobile
                              }
                            />
                            <Form.Control.Feedback type="invalid">
                              {errors.email_or_mobile}
                            </Form.Control.Feedback>
                          </Form.Group>
                        )}

                        <Form.Group className="mb-3">
                          <Form.Label>OTP</Form.Label>
                          <Form.Control
                            type="text"
                            name="otp"
                            value={values.otp}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            placeholder="Enter 6-digit OTP"
                            maxLength="6"
                            isInvalid={touched.otp && errors.otp}
                          />
                          <Form.Control.Feedback type="invalid">
                            {errors.otp}
                          </Form.Control.Feedback>
                        </Form.Group>

                        <Form.Group className="mb-3">
                          <Form.Label>New Password</Form.Label>
                          <InputGroup>
                            <InputGroup.Text>
                              <FaLock />
                            </InputGroup.Text>
                            <Form.Control
                              type={showPassword ? "text" : "password"}
                              name="new_password"
                              value={values.new_password}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              placeholder="Enter new password"
                              isInvalid={
                                touched.new_password && errors.new_password
                              }
                            />
                            <Button
                              variant="outline-secondary"
                              onClick={() => setShowPassword(!showPassword)}
                            >
                              {showPassword ? <FaEyeSlash /> : <FaEye />}
                            </Button>
                            <Form.Control.Feedback type="invalid">
                              {errors.new_password}
                            </Form.Control.Feedback>
                          </InputGroup>
                          <Form.Text muted>
                            At least 6 characters with letter, number, and
                            special character
                          </Form.Text>
                        </Form.Group>

                        <Form.Group className="mb-4">
                          <Form.Label>Confirm New Password</Form.Label>
                          <InputGroup>
                            <InputGroup.Text>
                              <FaLock />
                            </InputGroup.Text>
                            <Form.Control
                              type={showConfirmPassword ? "text" : "password"}
                              name="confirm_password"
                              value={values.confirm_password}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              placeholder="Confirm new password"
                              isInvalid={
                                touched.confirm_password &&
                                errors.confirm_password
                              }
                            />
                            <Button
                              variant="outline-secondary"
                              onClick={() =>
                                setShowConfirmPassword(!showConfirmPassword)
                              }
                            >
                              {showConfirmPassword ? <FaEyeSlash /> : <FaEye />}
                            </Button>
                            <Form.Control.Feedback type="invalid">
                              {errors.confirm_password}
                            </Form.Control.Feedback>
                          </InputGroup>
                        </Form.Group>

                        <Button
                          variant="success"
                          type="submit"
                          className="w-100"
                          disabled={loading || !isValid}
                        >
                          {loading ? "Resetting Password..." : "Reset Password"}
                        </Button>
                      </Form>
                    )}
                  </Formik>
                </>
              )}

              <div className="text-center mt-3">
                <span className="text-muted">Remember your password? </span>
                <Link to="/login" className="text-decoration-none fw-bold">
                  Login here
                </Link>
              </div>
            </Card.Body>
          </Card>

          <div className="text-center mt-4">
            <div className="alert alert-warning">
              <small>
                <strong>Security Tip:</strong> Use a strong, unique password
                that you haven't used elsewhere.
              </small>
            </div>
          </div>
        </Col>
      </Row>
    </Container>
  );
};

export default ResetPassword;
